import { Activity, ActivityType } from '../types';
import { TeamMember } from '../types/teamSummary';
import { subDays, subHours, subMinutes, format } from 'date-fns';

// Course names for mock data
const COURSE_NAMES = [
  'Introduction to React',
  'Advanced JavaScript',
  'TypeScript Fundamentals',
  'Node.js Essentials',
  'Database Design',
  'API Development',
  'UI/UX Design Principles',
  'Project Management',
  'Agile Methodologies',
  'DevOps Basics',
];

/**
 * Generates mock activity data for a team member based on their existing data
 * @param member - The team member to generate activities for
 * @param days - Number of days to look back (default: 30)
 * @returns Array of activities sorted by timestamp (newest first)
 */
export function generateMockActivities(member: TeamMember, days: number = 30): Activity[] {
  const activities: Activity[] = [];
  const now = new Date();
  const startDate = subDays(now, days);
  
  let activityId = 1;

  // Generate course started activities
  // Assume user started more courses than completed
  const coursesStarted = member.totalCoursesCompleted + Math.floor(Math.random() * 3) + 1;
  for (let i = 0; i < coursesStarted; i++) {
    const courseName = COURSE_NAMES[i % COURSE_NAMES.length];
    const timestamp = new Date(
      startDate.getTime() + Math.random() * (now.getTime() - startDate.getTime())
    );
    
    activities.push({
      id: `activity-${activityId++}`,
      type: 'course_started',
      title: 'Course Started',
      description: `Started "${courseName}"`,
      timestamp: timestamp.toISOString(),
      metadata: { courseName },
    });
  }

  // Generate course completed activities
  for (let i = 0; i < member.totalCoursesCompleted; i++) {
    const courseName = COURSE_NAMES[i % COURSE_NAMES.length];
    // Course completion should be after course start
    const timestamp = new Date(
      startDate.getTime() + Math.random() * (now.getTime() - startDate.getTime())
    );
    
    activities.push({
      id: `activity-${activityId++}`,
      type: 'course_completed',
      title: 'Course Completed',
      description: `Completed "${courseName}"`,
      timestamp: timestamp.toISOString(),
      metadata: { courseName },
    });
  }

  // Generate badge earned activities
  member.badges?.forEach((badge) => {
    if (!badge.earnedDate) return;
    const badgeDate = new Date(badge.earnedDate);
    // Only include badges from the last 30 days
    if (badgeDate >= startDate) {
      activities.push({
        id: `activity-${activityId++}`,
        type: 'badge_earned',
        title: 'Badge Earned',
        description: `Earned "${badge.name}" badge`,
        timestamp: badge.earnedDate,
        metadata: { badgeName: badge.name },
      });
    }
  });

  // Generate certification completed activities
  member.certifications?.forEach((cert) => {
    const certDate = new Date(cert.completedDate);
    // Only include certifications from the last 30 days
    if (certDate >= startDate) {
      activities.push({
        id: `activity-${activityId++}`,
        type: 'certification_completed',
        title: 'Certification Completed',
        description: `Completed "${cert.name}" certification`,
        timestamp: cert.completedDate,
        metadata: { certificationName: cert.name },
      });
    }
  });

  // Generate time active activities (learning sessions)
  // Distribute hours across multiple sessions
  const totalMinutes = member.hoursInCourses * 60;
  const sessionCount = Math.floor(totalMinutes / 30) + Math.floor(Math.random() * 5); // 30+ min sessions
  const minutesPerSession = Math.floor(totalMinutes / sessionCount);
  
  for (let i = 0; i < sessionCount; i++) {
    const timestamp = new Date(
      startDate.getTime() + Math.random() * (now.getTime() - startDate.getTime())
    );
    const duration = minutesPerSession + Math.floor(Math.random() * 30); // 30-60 min sessions
    
    activities.push({
      id: `activity-${activityId++}`,
      type: 'time_active',
      title: 'Active Learning Session',
      description: `Studied for ${duration} minutes`,
      timestamp: timestamp.toISOString(),
      metadata: { duration },
    });
  }

  // Sort by timestamp (newest first)
  return activities.sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

/**
 * Simulates async API call to fetch activity data
 * @param member - The team member to fetch activities for
 * @returns Promise that resolves with activity array
 */
export async function fetchActivityData(member: TeamMember): Promise<Activity[]> {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return generateMockActivities(member, 30);
}

